/** The ONE place that turns raw site signals into a dot colour and a word.
 *
 *  Built because the console was telling the operator two different things about
 *  the same site: `/admin/sites` said **offline** while the site switcher said
 *  **critical**, and neither said which question it was answering. Both were
 *  right — about different axes — and that is precisely why it read as a bug.
 *
 *  **There are two independent axes and they must never be collapsed:**
 *
 *  - **Reachability** — can the cloud reach the box? Derived from the server
 *    announce (`last_seen`, ~15 s cadence, 45 s window). A site can be perfectly
 *    healthy and unreachable: that is a NETWORK fault, not a crop fault.
 *  - **Health** — is the farm in trouble? Derived cloud-side from alarms and
 *    telemetry freshness (`classify_site_health`). A site with a failed FCU is
 *    very much online — being online is how you heard about it.
 *
 *  Plus one flag that outranks both: **disabled**, an administrator switching the
 *  site off. Intentional, so it is never dressed as a fault.
 *
 *  ⚠ **The two screens do not have the same information, and this module does not
 *  pretend otherwise.** `/users/v1/overview` is plain-auth so every cloud user can
 *  read HEALTH, but `list_servers` is admin-only, so the site switcher can never
 *  read REACHABILITY. `reachability: 'unknown'` is therefore a real, common answer
 *  — not a loading state — and a caller that cannot see an axis must stay silent
 *  about it rather than guess. Making both screens show both axes needs
 *  reachability added to the overview payload; that is the next phase. */

export type Reachability = 'online' | 'offline' | 'unknown';

/** Mirrors `classify_site_health` in `manager-cloud`. Keep the two in step. */
export type SiteHealth = 'healthy' | 'warning' | 'critical' | 'stale' | 'disabled' | 'unknown';

/** The dot colour. Four tones, so a fifth opinion cannot appear per screen. */
export type StatusTone = 'ok' | 'warn' | 'danger' | 'muted';

export interface SiteSignals {
  /** `server_registrations.status`. `null`/absent = no box registered (manual site). */
  serverStatus?: 'active' | 'disabled' | string | null | undefined;
  /** From the servers list. `undefined` = this caller cannot see it (non-admin). */
  online?: boolean | undefined;
  /** Is a box registered at all? A manual site has none, so it is not "offline". */
  hasServer?: boolean | undefined;
  /** From the fleet overview. */
  health?: SiteHealth | undefined;
}

export interface SiteStatusView {
  disabled: boolean;
  reachability: Reachability;
  health: SiteHealth;
  /** The dot. Derived once, here. */
  tone: StatusTone;
  /** i18n key (namespace `siteStatus`) for the reachability word, or `null` when
   *  this caller cannot know it — in which case say NOTHING about reachability. */
  reachKey: 'reachOnline' | 'reachOffline' | 'reachNoBox' | null;
  /** i18n key for the health word, or `null` when health is unknown. */
  healthKey: 'healthOk' | 'healthWarning' | 'healthCritical' | 'healthNoSignal' | null;
}

const HEALTH_TONE: Record<SiteHealth, StatusTone> = {
  healthy: 'ok',
  warning: 'warn',
  critical: 'danger',
  // "We stopped hearing from it" — an absence of information, not a red alarm.
  stale: 'muted',
  // Administratively off. Amber rather than grey, matching what both screens
  // already showed, so this change does not silently restyle a third thing.
  disabled: 'warn',
  unknown: 'muted',
};

const HEALTH_KEY: Record<SiteHealth, SiteStatusView['healthKey']> = {
  healthy: 'healthOk',
  warning: 'healthWarning',
  critical: 'healthCritical',
  stale: 'healthNoSignal',
  disabled: null, // the disabled flag is reported on its own, not as a health word
  unknown: null,
};

/**
 * Derive the canonical view. **Both screens must call this** — if either derives
 * a dot or a word itself, they will drift apart again.
 */
export function siteStatus(s: SiteSignals): SiteStatusView {
  const disabled = s.serverStatus === 'disabled';
  const hasServer = s.hasServer ?? s.serverStatus != null;

  const reachability: Reachability =
    !hasServer || s.online === undefined ? 'unknown' : s.online ? 'online' : 'offline';

  const health: SiteHealth = disabled ? 'disabled' : (s.health ?? 'unknown');

  // Precedence, and each step is a deliberate choice:
  // 1. `disabled` outranks everything — it matches the backend classifier, and a
  //    site someone switched off must not be reported as a fault.
  // 2. A box we cannot reach outranks its health, because that health is
  //    last-known rather than current. Grey says "we cannot see", which is the
  //    honest claim; red would assert a live crop problem we cannot observe.
  // 3. Otherwise the health axis decides.
  let tone: StatusTone;
  if (disabled) tone = HEALTH_TONE.disabled;
  else if (reachability === 'offline') tone = 'muted';
  else tone = HEALTH_TONE[health];

  const reachKey: SiteStatusView['reachKey'] = !hasServer
    ? 'reachNoBox'
    : reachability === 'unknown'
      ? null
      : reachability === 'online'
        ? 'reachOnline'
        : 'reachOffline';

  return { disabled, reachability, health, tone, reachKey, healthKey: HEALTH_KEY[health] };
}
